import {
	Component,
	ComponentInterface,
	Element,
	Listen,
	Prop,
} from '@stencil/core';
import { StatCardGridAppearance } from '../../../../_storybook-helpers/cards';
import { RowSizes } from '../hero-number-card/hero-number-card.interface';
import { getChildComponents } from '../_helpers/prefixes';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../_helpers/content-security-policy';
@Component({
	tag: 'ds-stat-card-grid',
})
export class StatCardGrid implements ComponentInterface {
	@Element() el: HTMLDsStatCardGridElement;
	heroNumberCards: HTMLDsHeroNumberCardElement[];
	prefixes: NodeListOf<HTMLElement>;
	numbers: NodeListOf<Element>;
	suffixes: NodeListOf<Element>;
	statCardRows: HTMLDsStatCardRowElement[];

	@Listen('fontSizeChange')
	fontSizeChangeHandler() {
		this.updateHeroNumberCardsFontSizes();
	}

	@Prop() appearance: StatCardGridAppearance = 'full-width';

	componentWillLoad(): void {
		this.heroNumberCards = getChildComponents(this.el, 'ds-hero-number-card');
		this.prefixes = this.el.querySelectorAll('.ds-prefix');
		this.numbers = this.el.querySelectorAll('.ds-number');
		this.suffixes = this.el.querySelectorAll('.ds-suffix');
		this.statCardRows = getChildComponents(this.el, 'ds-stat-card-row');
		this.configureRows();
	}

	componentDidLoad() {
		this.updateHeroNumberCardsFontSizes();
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	configureRows(): void {
		if (this.statCardRows) {
			this.statCardRows.forEach(
				(row: HTMLDsStatCardRowElement, rowIndex: number) => {
					if (row.keyLinesOption === 'divider') {
						// The last row needs different behaviour for divider lines showing in the different breakpoints
						row.isLastRow = rowIndex === this.statCardRows.length - 1;

						const nextRow = this.statCardRows[rowIndex + 1];
						row.nextRowKeyLinesOption = nextRow ? nextRow.keyLinesOption : null;
					}
				}
			);
		}
	}

	/*
	 * 	Calculate and set font-sizes and line-heights for Hero Number Card's number section.
	 *
	 *	The calculation depends on Hero Number Card size changes. If window is resized,
	 *	breakpoints are changing or one of the cards has longer number then others, the child components (Hero Number Cards)
	 *	are sending their actual sizes to parent component and then the parent takes the smallest of those sizes and applies it to all children.
	 */
	updateHeroNumberCardsFontSizes(): void {
		const rowSizes = [];

		// iterate through Stat Card Grid component's card list
		this.heroNumberCards.forEach((card): RowSizes[] => {
			// check if prefix-, number- and suffix font-sizes exist, then convert them to number
			if (
				card.rowSizes?.prefixFontSize &&
				card.rowSizes?.numberFontSize &&
				card.rowSizes?.suffixFontSize &&
				typeof card.rowSizes.prefixFontSize === 'string' &&
				typeof card.rowSizes.numberFontSize === 'string' &&
				typeof card.rowSizes.suffixFontSize === 'string'
			) {
				rowSizes.push({
					prefixFontSize: parseFloat(card.rowSizes.prefixFontSize),
					numberFontSize: parseFloat(card.rowSizes.numberFontSize),
					suffixFontSize: parseFloat(card.rowSizes.suffixFontSize),
				});
			}
			return rowSizes;
		});

		// create an object which contains the smallest sizes
		const heroCardFontSizes = {
			prefixFontSize: `${Math.min(
				...rowSizes.map((item) => item.prefixFontSize)
			)}px`,
			numberFontSize: `${Math.min(
				...rowSizes.map((item) => item.numberFontSize)
			)}px`,
			suffixFontSize: `${Math.min(
				...rowSizes.map((item) => item.suffixFontSize)
			)}px`,
		};
		// update Hero Number Card sizes with the minimums
		this.prefixes.forEach((prefix: HTMLElement): void => {
			prefix.style.fontSize = heroCardFontSizes.prefixFontSize;
			prefix.style.lineHeight = heroCardFontSizes.prefixFontSize;
		});
		this.numbers.forEach((number: HTMLElement): void => {
			number.style.fontSize = heroCardFontSizes.numberFontSize;
			number.style.lineHeight = heroCardFontSizes.numberFontSize;
		});
		this.suffixes.forEach((suffix: HTMLElement): void => {
			suffix.style.fontSize = heroCardFontSizes.suffixFontSize;
			suffix.style.lineHeight = heroCardFontSizes.suffixFontSize;
		});
		moveInlineStylesToHead(this.el.id);
	}
}
